import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Hub } from 'aws-amplify';
import { AppShell, ErrorBoundary, Logo, Footer } from '@bafkal/ui-shared/components';
import { BafkalThemeProvider } from '@bafkal/ui-shared/theme';
import { colors } from '@bafkal/ui-shared/tokens';
import cognitoConfig from '../shared/config/cognitoConfig';
import Login from '../features/auth/components/Login';
import Register from '../features/auth/components/Register';
import Dashboard from '../features/dashboard/Dashboard';
import AdForm from '../features/inventory/components/AdForm';
import InventoryPage from '../features/inventory/components/InventoryPage';
import PlacementForm from '../features/inventory/components/PlacementForm';
import PaymentSettings from '../features/payments/PaymentSettings';
import PublisherSettingsForm from '../features/settings/components/PublisherSettingsForm';
import PrivateRoute from '../features/auth/components/PrivateRoute';
import { checkAuth, logout } from '../features/auth/authActions';
import { LOGOUT } from '../store/types';
import { APP_ROUTES, AMPLIFY_HUB_EVENTS, AMPLIFY_HUB_CHANNELS, ENV } from '../shared/constants';
import './App.css';

/**
 * AppLayout — rendered inside <Router> so it can use useLocation.
 * When authenticated, wraps routes in AppShell (shared header + mobile nav).
 * When not authenticated, routes render bare (login / register pages own their layout).
 */
function AppLayout() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const location = useLocation();

  const handleLogout = () => dispatch(logout({ hostedLogout: true, silent: true }));

  const navLinks = [
    {
      label: 'Dashboard',
      href: APP_ROUTES.DASHBOARD,
      active: location.pathname === APP_ROUTES.DASHBOARD,
    },
    {
      label: 'Inventory',
      href: APP_ROUTES.INVENTORY.ROOT,
      active: location.pathname.startsWith(APP_ROUTES.INVENTORY.ROOT),
    },
    {
      label: 'Payments',
      href: APP_ROUTES.PAYMENT,
      active: location.pathname === APP_ROUTES.PAYMENT,
    },
    {
      label: 'Settings',
      href: APP_ROUTES.SETTINGS,
      active: location.pathname === APP_ROUTES.SETTINGS,
    },
  ];

  // Provide the router-aware Link to AppShell so navigation stays SPA (no hard reload).
  const renderLink = (link, style) => (
    <Link key={link.href} to={link.href} style={style} onClick={link.onClick}>
      {link.label}
    </Link>
  );

  const logo = (
    <Link to={APP_ROUTES.DASHBOARD} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
      <Logo className="h-8 w-8 text-blue-600 mr-2" />
      <span style={{ fontSize: '1rem', color: colors.neutral[500], whiteSpace: 'pre' }}>
        Bafkal <span style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.primary[600] }}>Ad</span><span style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.neutral[900] }}>Publisher</span>
      </span>
    </Link>
  );

  const routes = (
    <Switch>
      <Route
        path={APP_ROUTES.LOGIN}
        render={(props) =>
          isAuthenticated ? <Redirect to={APP_ROUTES.DASHBOARD} /> : <Login {...props} />
        }
      />
      <Route
        path={APP_ROUTES.REGISTER}
        render={(props) =>
          isAuthenticated ? <Redirect to={APP_ROUTES.DASHBOARD} /> : <Register {...props} />
        }
      />
      <PrivateRoute path={APP_ROUTES.DASHBOARD} component={Dashboard} />
      {/* Inventory routes — most specific first */}
      <PrivateRoute path={APP_ROUTES.INVENTORY.PLACEMENT_NEW} component={PlacementForm} />
      <PrivateRoute path={APP_ROUTES.INVENTORY.PLACEMENT_EDIT(':id')} component={PlacementForm} />
      <PrivateRoute path={APP_ROUTES.INVENTORY.NEW} component={AdForm} />
      <PrivateRoute path={`${APP_ROUTES.INVENTORY.ROOT}/:id/edit`} component={AdForm} />
      <PrivateRoute path={APP_ROUTES.INVENTORY.PLACEMENTS} component={InventoryPage} />
      <PrivateRoute path={APP_ROUTES.INVENTORY.ROOT} component={InventoryPage} />
      <PrivateRoute path={APP_ROUTES.PAYMENT} component={PaymentSettings} />
      <PrivateRoute path={APP_ROUTES.SETTINGS} component={PublisherSettingsForm} />
      <Route path={APP_ROUTES.HOME} exact>
        <Redirect to={APP_ROUTES.DASHBOARD} />
      </Route>
    </Switch>
  );

  if (!isAuthenticated) {
    // Unauthenticated: render only the public routes without the shell.
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <main style={{ flexGrow: 1 }}>{routes}</main>
      </div>
    );
  }

  return (
    <AppShell
      logo={logo}
      brandTitle=""
      navLinks={navLinks}
      user={user}
      onLogout={handleLogout}
      renderLink={renderLink}
      footer={<Footer />}
    >
      {routes}
    </AppShell>
  );
}

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // On app start, check existing Cognito session
    dispatch(checkAuth());

    // If the app was redirected back with an OAuth `code` but the app does not have
    // full oauth/Pool configuration, remove the query to avoid Amplify attempting to handle it.
    const search = window.location.search || '';
    const hasIncompleteConfig =
      !cognitoConfig.Auth ||
      !cognitoConfig.Auth.userPoolId ||
      !cognitoConfig.Auth.userPoolWebClientId ||
      !cognitoConfig.Auth.oauth ||
      !cognitoConfig.Auth.oauth.domain;

    if (search.includes('code') && hasIncompleteConfig) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Listen for auth events (signIn, signOut) and refresh auth state
    const authListener = (data) => {
      switch (data.payload.event) {
        case AMPLIFY_HUB_EVENTS.SIGN_IN:
          dispatch(checkAuth());
          break;
        case AMPLIFY_HUB_EVENTS.SIGN_OUT:
          // Avoid re-invoking the logout flow to prevent loops
          dispatch({ type: LOGOUT });
          break;
        default:
          break;
      }
    };

    // Listen for app-level auth signals (e.g., when API forced sign-out occurs)
    const appListener = (data) => {
      if (ENV.IS_DEVELOPMENT) {
        console.warn('[app Hub] received app event', data);
      }
      if (data?.payload?.event === AMPLIFY_HUB_EVENTS.SIGNED_OUT) {
        if (ENV.IS_DEVELOPMENT) {
          console.warn('[app Hub] signedOut event received, clearing auth state');
        }
        dispatch({ type: LOGOUT });
      }
    };

    Hub.listen(AMPLIFY_HUB_CHANNELS.AUTH, authListener);
    Hub.listen(AMPLIFY_HUB_CHANNELS.APP, appListener);

    return () => {
      Hub.remove(AMPLIFY_HUB_CHANNELS.AUTH, authListener);
      Hub.remove(AMPLIFY_HUB_CHANNELS.APP, appListener);
    };
  }, [dispatch]);

  return (
    <ErrorBoundary>
      <BafkalThemeProvider>
        <Router>
          <AppLayout />
        </Router>
      </BafkalThemeProvider>
    </ErrorBoundary>
  );
}

export default App;